import { useEffect, useState } from "react";
import { submitContact } from "@/lib/api";
import { toast } from "sonner";
import { Send, Mail, MapPin, Instagram } from "lucide-react";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        document.title = "Contact · MotorcycleRoutesIreland";
    }, []);

    const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in name, email and message.");
            return;
        }
        setSubmitting(true);
        try {
            await submitContact(form);
            setSent(true);
            toast.success("Message sent. We'll be in touch.");
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            toast.error("Couldn't send your message. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="pt-40 pb-16 border-b border-[var(--border-hair)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow fade-up">Say Hello</div>
                    <h1 className="mt-4 font-display font-black uppercase text-[var(--fg)] text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tighter fade-up fade-up-delay-1">
                        Drop us<br />a line.
                    </h1>
                    <p className="mt-8 font-body text-[var(--fg-muted)] text-lg max-w-2xl fade-up fade-up-delay-2">
                        Route suggestions, partnership ideas, corrections, cafe recommendations — all welcome. We answer everything, eventually.
                    </p>
                </div>
            </section>

            <section className="py-16 lg:py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-12">
                    <div className="space-y-10">
                        <ContactBlock icon={Mail} label="Email" value="hello@motorcycleroutesireland.com" />
                        <ContactBlock icon={MapPin} label="Based in" value="County Kerry, Ireland" />
                        <ContactBlock icon={Instagram} label="Instagram" value="@motorcycleroutesireland" />
                        <div className="pt-10 border-t border-[var(--border-hair)]">
                            <div className="eyebrow mb-4">Response Time</div>
                            <p className="font-body text-[var(--fg-muted)] leading-relaxed">
                                We usually reply within a few days. If it's urgent and involves a road being closed, mark the subject line.
                            </p>
                        </div>
                    </div>

                    <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Field label="Name" required>
                            <input
                                data-testid="contact-name"
                                value={form.name}
                                onChange={change("name")}
                                required
                                className="w-full bg-[var(--card-bg)] border border-[var(--border-hair)] focus:border-[#E87722] px-4 py-4 font-body text-[var(--fg)] outline-none transition-colors"
                            />
                        </Field>
                        <Field label="Email" required>
                            <input
                                data-testid="contact-email"
                                type="email"
                                value={form.email}
                                onChange={change("email")}
                                required
                                className="w-full bg-[var(--card-bg)] border border-[var(--border-hair)] focus:border-[#E87722] px-4 py-4 font-body text-[var(--fg)] outline-none transition-colors"
                            />
                        </Field>
                        <div className="md:col-span-2">
                            <Field label="Subject">
                                <input
                                    data-testid="contact-subject"
                                    value={form.subject}
                                    onChange={change("subject")}
                                    className="w-full bg-[var(--card-bg)] border border-[var(--border-hair)] focus:border-[#E87722] px-4 py-4 font-body text-[var(--fg)] outline-none transition-colors"
                                />
                            </Field>
                        </div>
                        <div className="md:col-span-2">
                            <Field label="Message" required>
                                <textarea
                                    data-testid="contact-message"
                                    value={form.message}
                                    onChange={change("message")}
                                    required
                                    rows={8}
                                    className="w-full bg-[var(--card-bg)] border border-[var(--border-hair)] focus:border-[#E87722] px-4 py-4 font-body text-[var(--fg)] outline-none transition-colors resize-none"
                                />
                            </Field>
                        </div>
                        <div className="md:col-span-2 flex items-center justify-between gap-6 flex-wrap">
                            <p className="text-xs text-[var(--fg-dim)] font-body max-w-md">
                                By sending you agree to be contacted about your query. We don't share your details.
                            </p>
                            <button
                                type="submit"
                                data-testid="contact-submit-btn"
                                disabled={submitting}
                                className="inline-flex items-center gap-3 bg-[#E87722] hover:bg-[#F28C38] disabled:opacity-50 disabled:cursor-not-allowed text-black px-8 py-4 font-display font-bold uppercase tracking-[0.2em] text-sm transition-all"
                            >
                                {submitting ? "Sending…" : (<>Send Message <Send size={16} /></>)}
                            </button>
                        </div>
                        {sent && (
                            <div data-testid="contact-success" className="md:col-span-2 border border-[#E87722] bg-[#E87722]/10 px-5 py-4 font-body text-[#F28C38]">
                                Thanks — your message is with us. We'll reply as soon as we're off the bike.
                            </div>
                        )}
                    </form>
                </div>
            </section>
        </div>
    );
}

const Field = ({ label, required, children }) => (
    <label className="block">
        <span className="eyebrow block mb-2 !text-[var(--fg-muted)]">{label}{required && <span className="text-[#E87722] ml-1">*</span>}</span>
        {children}
    </label>
);

const ContactBlock = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-4">
        <div className="w-12 h-12 border border-[var(--border-hair)] flex items-center justify-center text-[#E87722] flex-shrink-0">
            <Icon size={18} />
        </div>
        <div>
            <div className="eyebrow !text-[var(--fg-dim)]">{label}</div>
            <div className="mt-1 font-body text-[var(--fg)]">{value}</div>
        </div>
    </div>
);
